import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSocietyAdminData,
  getComplaintsBySociety,
  getComplaintById,
  updateComplaintStatus,
  getComplaintMessages,
  postComplaintMessage,
  Complaint,
  ComplaintMessage,
  ComplaintStatus,
  ComplaintCategory,
} from '../services/api';
import {
  HiExclamation,
  HiRefresh,
  HiSearch,
  HiChevronRight,
  HiX,
  HiCheck,
  HiDocumentText,
  HiUsers,
} from '../components/Icons';
import './SocietyAdminComplaints.css';

type ViewMode = 'active' | 'archive';

const CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  carpentry: 'Carpentry',
  housekeeping: 'Housekeeping',
  security: 'Security',
  parking: 'Parking',
  noise: 'Noise',
  common_area: 'Common Area',
  elevator: 'Elevator',
  water_supply: 'Water Supply',
  other: 'Other',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const ALL_CATEGORIES: ComplaintCategory[] = [
  'plumbing', 'electrical', 'carpentry', 'housekeeping', 'security',
  'parking', 'noise', 'common_area', 'elevator', 'water_supply', 'other',
];

const ACTIVE_STATUSES: ComplaintStatus[] = ['open', 'in_progress'];
const ARCHIVE_STATUSES: ComplaintStatus[] = ['resolved', 'closed'];

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return (
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  );
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
};

const statusNextOptions: Record<ComplaintStatus, ComplaintStatus[]> = {
  open: ['in_progress', 'resolved', 'closed'],
  in_progress: ['open', 'resolved', 'closed'],
  resolved: ['open', 'closed'],
  closed: ['open'],
};

const SocietyAdminComplaints: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [viewMode, setViewMode] = useState<ViewMode>('active');
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filterCategory, setFilterCategory] = useState<ComplaintCategory | ''>('');
  const [filterStatus, setFilterStatus] = useState<ComplaintStatus | ''>('');
  const [search, setSearch] = useState('');

  // Detail panel
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Complaint | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Messages
  const [messages, setMessages] = useState<ComplaintMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusActionMenu, setStatusActionMenu] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState('');
  const [remarksFor, setRemarksFor] = useState<ComplaintStatus | null>(null);
  const [statusAlert, setStatusAlert] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // KPIs
  const [kpis, setKpis] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });

  const fetchComplaints = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Fetch all to compute KPIs and display
      const res = await getComplaintsBySociety(societyId, {
        category: filterCategory || undefined,
      });
      const all = res.data ?? [];
      setKpis({
        open: all.filter(c => c.status === 'open').length,
        in_progress: all.filter(c => c.status === 'in_progress').length,
        resolved: all.filter(c => c.status === 'resolved').length,
        closed: all.filter(c => c.status === 'closed').length,
      });
      const active = ACTIVE_STATUSES;
      const archive = ARCHIVE_STATUSES;
      const statusFilter = viewMode === 'active' ? active : archive;
      let filtered = all.filter(c => statusFilter.includes(c.status));
      if (filterStatus) filtered = filtered.filter(c => c.status === filterStatus);
      setComplaints(filtered);
    } catch (e: any) {
      setError(e.message || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [societyId, filterCategory, filterStatus, viewMode]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const fetchDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const [compRes, msgRes] = await Promise.all([
        getComplaintById(id),
        getComplaintMessages(id),
      ]);
      setDetail(compRes.data);
      setMessages(msgRes.data ?? []);
    } catch (e: any) {
      setDetailError(e.message || 'Failed to load complaint');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else { setDetail(null); setMessages([]); }
  }, [selectedId, fetchDetail]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!selectedId || !newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const res = await postComplaintMessage(selectedId, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (e: any) {
      setStatusAlert({ kind: 'err', text: e.message || 'Failed to send message' });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStatusChange = async (status: ComplaintStatus) => {
    if (!selectedId) return;
    setUpdatingStatus(true);
    setStatusAlert(null);
    try {
      const res = await updateComplaintStatus(selectedId, status, adminRemarks || undefined);
      setDetail(res.data);
      // Update in list
      setComplaints(prev => prev.map(c => c.id === selectedId ? res.data : c));
      // Refresh KPIs
      fetchComplaints();
      setStatusAlert({ kind: 'ok', text: `Status updated to "${STATUS_LABELS[status]}"` });
      setRemarksFor(null);
      setAdminRemarks('');
      setStatusActionMenu(false);
    } catch (e: any) {
      setStatusAlert({ kind: 'err', text: e.message || 'Failed to update status' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Filtered + searched list
  const displayed = complaints.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.title.toLowerCase().includes(q) ||
      (c.user_name || '').toLowerCase().includes(q) ||
      (c.flat_number || '').toLowerCase().includes(q) ||
      (c.block_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="sac-page">
      {/* KPI Strip */}
      <div className="sac-kpi-strip">
        <div className="sac-kpi sac-kpi-open">
          <span className="sac-kpi-val">{kpis.open}</span>
          <span className="sac-kpi-label">Open</span>
        </div>
        <div className="sac-kpi sac-kpi-inprogress">
          <span className="sac-kpi-val">{kpis.in_progress}</span>
          <span className="sac-kpi-label">In Progress</span>
        </div>
        <div className="sac-kpi sac-kpi-resolved">
          <span className="sac-kpi-val">{kpis.resolved}</span>
          <span className="sac-kpi-label">Resolved</span>
        </div>
        <div className="sac-kpi sac-kpi-closed">
          <span className="sac-kpi-val">{kpis.closed}</span>
          <span className="sac-kpi-label">Closed</span>
        </div>
      </div>

      <div className="sac-body">
        {/* Left pane: list */}
        <div className="sac-list-pane">
          {/* View mode tabs */}
          <div className="sac-tabs-bar">
            <div className="sac-tabs">
              <button
                className={`sac-tab ${viewMode === 'active' ? 'sac-tab-active' : ''}`}
                onClick={() => { setViewMode('active'); setSelectedId(null); setFilterStatus(''); }}
              >
                <HiDocumentText size={16} /> Active
                {kpis.open + kpis.in_progress > 0 && (
                  <span className="sac-tab-badge">{kpis.open + kpis.in_progress}</span>
                )}
              </button>
              <button
                className={`sac-tab ${viewMode === 'archive' ? 'sac-tab-active' : ''}`}
                onClick={() => { setViewMode('archive'); setSelectedId(null); setFilterStatus(''); }}
              >
                <HiUsers size={16} /> Archive
                {kpis.resolved + kpis.closed > 0 && (
                  <span className="sac-tab-badge sac-tab-badge-gray">{kpis.resolved + kpis.closed}</span>
                )}
              </button>
            </div>
            <button
              className="sac-btn sac-btn-outline sac-btn-sm"
              onClick={fetchComplaints}
              disabled={loading}
              title="Refresh"
            >
              <HiRefresh size={15} />
            </button>
          </div>

          {/* Filters */}
          <div className="sac-filters">
            <div className="sac-search-wrap">
              <HiSearch size={15} className="sac-search-icon" />
              <input
                className="sac-search"
                type="text"
                placeholder="Search by title, resident or flat…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="sac-search-clear" onClick={() => setSearch('')}>
                  <HiX size={13} />
                </button>
              )}
            </div>
            <select
              className="sac-select"
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value as ComplaintCategory | '')}
            >
              <option value="">All categories</option>
              {ALL_CATEGORIES.map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            <select
              className="sac-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as ComplaintStatus | '')}
            >
              <option value="">All statuses</option>
              {(viewMode === 'active' ? ACTIVE_STATUSES : ARCHIVE_STATUSES).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* List */}
          {loading && (
            <div className="sac-list-loading">
              <HiRefresh size={22} className="spin" /> Loading…
            </div>
          )}
          {error && !loading && (
            <div className="sac-list-error">
              <HiExclamation size={18} /> {error}
            </div>
          )}
          {!loading && !error && displayed.length === 0 && (
            <div className="sac-list-empty">
              <HiDocumentText size={36} />
              <p>No complaints found</p>
            </div>
          )}
          <div className="sac-list">
            {displayed.map(c => (
              <button
                key={c.id}
                className={`sac-card ${selectedId === c.id ? 'sac-card-active' : ''}`}
                onClick={() => setSelectedId(c.id)}
              >
                <div className="sac-card-top">
                  <span className={`sac-badge sac-status-${c.status}`}>{STATUS_LABELS[c.status]}</span>
                  <span className={`sac-badge sac-priority-${c.priority}`}>{PRIORITY_LABELS[c.priority]}</span>
                  <span className="sac-card-time">{timeAgo(c.created_at)}</span>
                </div>
                <div className="sac-card-title">{c.title}</div>
                <div className="sac-card-meta">
                  <span>{CATEGORY_LABELS[c.category]}</span>
                  {(c.block_name || c.flat_number) && (
                    <span className="sac-card-flat">
                      {c.block_name ? `${c.block_name} – ` : ''}{c.flat_number}
                    </span>
                  )}
                  {c.user_name && <span className="sac-card-user">{c.user_name}</span>}
                </div>
                <HiChevronRight size={16} className="sac-card-chevron" />
              </button>
            ))}
          </div>
        </div>

        {/* Right pane: detail */}
        <div className={`sac-detail-pane ${selectedId ? 'sac-detail-pane-open' : ''}`}>
          {!selectedId && (
            <div className="sac-detail-empty">
              <HiDocumentText size={48} />
              <p>Select a complaint to view details</p>
            </div>
          )}

          {selectedId && detailLoading && (
            <div className="sac-detail-loading">
              <HiRefresh size={28} className="spin" /> Loading…
            </div>
          )}

          {selectedId && detailError && !detailLoading && (
            <div className="sac-detail-error">
              <HiExclamation size={20} /> {detailError}
            </div>
          )}

          {selectedId && detail && !detailLoading && (
            <div className="sac-detail">
              {/* Detail header */}
              <div className="sac-detail-header">
                <div className="sac-detail-title-row">
                  <h3 className="sac-detail-title">{detail.title}</h3>
                  <button
                    className="sac-close-btn"
                    onClick={() => setSelectedId(null)}
                    title="Close"
                  >
                    <HiX size={18} />
                  </button>
                </div>
                <div className="sac-detail-badges">
                  <span className={`sac-badge sac-status-${detail.status}`}>{STATUS_LABELS[detail.status]}</span>
                  <span className={`sac-badge sac-priority-${detail.priority}`}>{PRIORITY_LABELS[detail.priority]}</span>
                  <span className="sac-badge sac-badge-cat">{CATEGORY_LABELS[detail.category]}</span>
                </div>
              </div>

              {/* Meta info */}
              <div className="sac-detail-meta">
                <div className="sac-meta-row">
                  <span className="sac-meta-label">Raised by</span>
                  <span className="sac-meta-val">
                    {detail.user_name || '—'}
                    {detail.user_phone ? ` · ${detail.user_phone}` : ''}
                  </span>
                </div>
                {(detail.block_name || detail.flat_number) && (
                  <div className="sac-meta-row">
                    <span className="sac-meta-label">Flat</span>
                    <span className="sac-meta-val">
                      {detail.block_name ? `${detail.block_name} – ` : ''}{detail.flat_number}
                    </span>
                  </div>
                )}
                <div className="sac-meta-row">
                  <span className="sac-meta-label">Raised on</span>
                  <span className="sac-meta-val">{formatDateTime(detail.created_at)}</span>
                </div>
                {detail.resolved_at && (
                  <div className="sac-meta-row">
                    <span className="sac-meta-label">Resolved on</span>
                    <span className="sac-meta-val">{formatDateTime(detail.resolved_at)}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="sac-detail-section">
                <div className="sac-section-label">Description</div>
                <p className="sac-detail-desc">{detail.description}</p>
              </div>

              {/* Admin remarks (from backend, last saved) */}
              {detail.admin_remarks && (
                <div className="sac-detail-section">
                  <div className="sac-section-label">Admin Remarks</div>
                  <p className="sac-detail-remarks">{detail.admin_remarks}</p>
                </div>
              )}

              {/* Status actions */}
              <div className="sac-detail-section sac-status-section">
                <div className="sac-section-label">Update Status</div>

                {statusAlert && (
                  <div className={`sac-status-alert ${statusAlert.kind === 'err' ? 'sac-status-alert-err' : ''}`}>
                    {statusAlert.kind === 'ok' ? <HiCheck size={15} /> : <HiExclamation size={15} />}
                    {statusAlert.text}
                  </div>
                )}

                {remarksFor ? (
                  <div className="sac-remarks-form">
                    <div className="sac-remarks-heading">
                      Mark as <strong>{STATUS_LABELS[remarksFor]}</strong>
                    </div>
                    <textarea
                      className="sac-remarks-textarea"
                      placeholder="Add admin remarks (optional)…"
                      value={adminRemarks}
                      onChange={e => setAdminRemarks(e.target.value)}
                      rows={3}
                    />
                    <div className="sac-remarks-actions">
                      <button
                        className="sac-btn sac-btn-primary"
                        disabled={updatingStatus}
                        onClick={() => handleStatusChange(remarksFor)}
                      >
                        {updatingStatus ? 'Updating…' : `Confirm — ${STATUS_LABELS[remarksFor]}`}
                      </button>
                      <button
                        className="sac-btn sac-btn-ghost"
                        disabled={updatingStatus}
                        onClick={() => { setRemarksFor(null); setAdminRemarks(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="sac-status-actions">
                    {statusNextOptions[detail.status].map(s => (
                      <button
                        key={s}
                        className={`sac-btn sac-status-action-btn sac-status-btn-${s}`}
                        onClick={() => { setRemarksFor(s); setStatusAlert(null); }}
                        disabled={updatingStatus}
                      >
                        {STATUS_LABELS[s]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Message thread */}
              <div className="sac-detail-section sac-thread-section">
                <div className="sac-section-label">Thread</div>
                {messagesLoading && (
                  <div className="sac-thread-loading"><HiRefresh size={16} className="spin" /> Loading…</div>
                )}
                {!messagesLoading && messages.length === 0 && (
                  <p className="sac-thread-empty">No messages yet. Start a thread with the resident.</p>
                )}
                <div className="sac-thread-list">
                  {messages.map(m => (
                    <div key={m.id} className={`sac-msg sac-msg-${m.author_role}`}>
                      <div className="sac-msg-meta">
                        <span className="sac-msg-author">
                          {m.author_role === 'admin' ? '🔑 Admin' : `👤 ${m.user_name || 'Resident'}`}
                        </span>
                        <span className="sac-msg-time">{timeAgo(m.created_at)}</span>
                      </div>
                      <div className="sac-msg-body">{m.body}</div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="sac-thread-compose">
                  <textarea
                    className="sac-compose-textarea"
                    placeholder="Write a message to the resident…"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    rows={2}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    className="sac-btn sac-btn-primary"
                    disabled={!newMessage.trim() || sendingMessage}
                    onClick={handleSendMessage}
                  >
                    {sendingMessage ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocietyAdminComplaints;
